if (!customElements.get('video-modal')) {
  class VideoModal extends HTMLElement {
    constructor() {
      super();
      this.videoType = this.dataset.videoType;
      this.videoUrl = this.dataset.videoUrl || '';
      this.autoplay = this.dataset.autoplay !== 'false';
      this.opener = null;

      this.onKeydown = this.onKeydown.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.close = this.close.bind(this);
    }

    /**
     * Wires child element references and close-button listeners once the
     * element is connected.
     */
    connectedCallback() {
      if (this.frame) return;

      this.dialog = this.querySelector('.video-modal__dialog');
      this.frame = this.querySelector('.video-modal__frame');
      this.template = this.querySelector(':scope > template');

      this.querySelectorAll('[data-video-modal-close]').forEach((el) => {
        el.addEventListener('click', this.close);
      });
    }

    /**
     * Opens the modal and starts playback.
     * @param {Element} [opener] - Element that triggered the modal.
     */
    open(opener) {
      if (this.hasAttribute('open')) return;
      this.opener = opener || document.activeElement;

      this.injectVideo();
      this.setAttribute('open', '');
      document.documentElement.classList.add('video-modal-open');
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('focusin', this.onFocusIn);

      const closeBtn = this.querySelector('.video-modal__close');
      if (closeBtn) closeBtn.focus();
    }

    /**
     * Closes the modal and stops playback.
     */
    close() {
      if (!this.hasAttribute('open')) return;
      this.removeAttribute('open');
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('focusin', this.onFocusIn);
      this.clearVideo();

      if (!document.querySelector('video-modal[open]')) {
        document.documentElement.classList.remove('video-modal-open');
      }

      if (this.opener && typeof this.opener.focus === 'function') {
        this.opener.focus();
      }
    }

    /**
     * Handles keyboard interaction while the modal is open.
     * @param {KeyboardEvent} evt - Event object.
     */
    onKeydown(evt) {
      if (evt.key === 'Escape') {
        this.close();
      } else if (evt.key === 'Tab') {
        this.trapFocus(evt);
      }
    }

    /**
     * Safety net for the focus trap: cross-origin iframes (YouTube / Vimeo)
     * capture their own Tab events, so trapFocus on keydown never sees them.
     * Once the iframe runs out of internal focusables, focus pops out into
     * the next element after the iframe in the document — outside the modal.
     * This listener catches that case and pulls focus back to the close
     * button, restarting the cycle.
     * @param {FocusEvent} evt - Event object.
     */
    onFocusIn(evt) {
      if (this.contains(evt.target)) return;

      const closeBtn = this.querySelector('.video-modal__close');
      if (closeBtn) closeBtn.focus();
    }

    /**
     * Keeps keyboard focus inside the open modal.
     * @param {KeyboardEvent} evt - Event object.
     */
    trapFocus(evt) {
      const focusable = Array.from(
        this.querySelectorAll('button, a[href], iframe, video, [tabindex]:not([tabindex="-1"])')
      ).filter((el) => el.offsetParent !== null);

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault();
        first.focus();
      }
    }

    /**
     * Injects the relevant player into the frame (once per open).
     */
    injectVideo() {
      if (this.frame.childElementCount > 0) return;

      if (this.videoType === 'upload') {
        this.injectUploadedVideo();
      } else {
        this.injectExternalVideo();
      }
    }

    /**
     * Clones the uploaded <video> from the template and plays it.
     */
    injectUploadedVideo() {
      if (!this.template) return;
      this.frame.appendChild(this.template.content.cloneNode(true));

      const video = this.frame.querySelector('video');
      if (!video) return;

      video.setAttribute('playsinline', '');
      video.controls = true;

      if (this.autoplay) {
        // Mute the video to prevent autoplay from being blocked by the browser.
        video.muted = true;
        video.autoplay = true;
        video.play().catch(() => {});
      }
    }

    /**
     * Builds and injects a YouTube or Vimeo iframe player.
     */
    injectExternalVideo() {
      const source = this.parseUrl(this.videoUrl);
      if (!source) return;

      const iframe = document.createElement('iframe');
      iframe.src = source.embed;
      iframe.title = 'Video player';
      iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture; encrypted-media');
      iframe.setAttribute('allowfullscreen', '');
      this.frame.appendChild(iframe);
    }

    /**
     * Parses a video URL into a provider and an embed URL.
     * @param {string} url - Source URL (YouTube or Vimeo).
     * @returns {?{provider: string, embed: string}} Parsed source, or null
     *   when the URL is empty, unparseable, or an unsupported provider.
     */
    parseUrl(url) {
      if (!url) return null;
      const autoplayFlag = this.autoplay ? 1 : 0;

      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        // Prefer the `v` query param; fall back to the last path segment
        // for short links (youtu.be/ID), /embed/ID, /shorts/ID, etc.
        const vMatch = url.match(/[?&]v=([^&#]+)/);
        const id = vMatch
          ? vMatch[1]
          : url.split('#')[0].split('?')[0].split('/').filter(Boolean).pop();

        // YouTube IDs are exactly 11 chars of [A-Za-z0-9_-]; anything else
        // (empty, hostname, junk) is treated as an unparseable URL.
        if (!id || !/^[A-Za-z0-9_-]{11}$/.test(id)) return null;

        // mute=1 paired with autoplay=1 required for YouTube to honor
        // autoplay in a cross-origin iframe.
        return {
          provider: 'youtube',
          embed: `https://www.youtube.com/embed/${id}?autoplay=${autoplayFlag}&mute=${autoplayFlag}&rel=0&playsinline=1`
        };
      }

      if (url.includes('vimeo.com')) {
        const [path, query = ''] = url.split('#')[0].split('?');
        const segments = path.split('/').filter(Boolean);
        const idIndex = segments.findIndex((seg) => /^\d+$/.test(seg));
        if (idIndex === -1) return null;
        const id = segments[idIndex];

        // Unlisted videos carry a privacy hash, either as the path segment
        // after the id (vimeo.com/{id}/{hash}) or an `h` query parameter.
        let hash = '';
        const nextSeg = segments[idIndex + 1];
        if (nextSeg && /^[A-Za-z0-9]+$/.test(nextSeg)) hash = nextSeg;

        const hashParam = query.split('&').find((param) => param.startsWith('h='));
        if (hashParam) hash = hashParam.slice(2);

        // muted=1 paired with autoplay=1 required for Vimeo to honor
        // autoplay in a cross-origin iframe.
        let embed = `https://player.vimeo.com/video/${id}?autoplay=${autoplayFlag}&muted=${autoplayFlag}&playsinline=1&dnt=1`;
        if (hash) embed += `&h=${hash}`;

        return { provider: 'vimeo', embed };
      }

      // Unsupported source — only YouTube and Vimeo links are accepted.
      return null;
    }

    /**
     * Removes the injected player so playback stops immediately.
     */
    clearVideo() {
      this.frame.replaceChildren();
    }
  }

  customElements.define('video-modal', VideoModal);

  // Delegated trigger handling to work for any existing or future trigger.
  document.addEventListener('click', (evt) => {
    const trigger = evt.target.closest('[data-video-modal-trigger]');
    if (!trigger) return;

    const modal = document.getElementById(trigger.getAttribute('data-video-modal-trigger'));
    if (modal && typeof modal.open === 'function') {
      evt.preventDefault();
      modal.open(trigger);
    }
  });
}
